import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import { format } from "date-fns";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import SalesDetailsModal from "../SalesDetailsModal";

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: "800px",
  },
  filterSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  tableContainer: {
    maxHeight: "400px",
    overflowY: "auto",
  },
  successCell: {
    color: theme.palette.success.main,
    fontWeight: "bold",
  },
  warningCell: {
    color: theme.palette.warning.main,
    fontWeight: "bold",
  },
  errorCell: {
    color: theme.palette.error.main,
    fontWeight: "bold",
  },
  totalRow: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: "bold",
  },
}));

const SalesReportModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [reportData, setReportData] = useState([]);
  
  // Modal de detalhes
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
  
  // Filtros
  const [selectedUserId, setSelectedUserId] = useState("");
  const [startDate, setStartDate] = useState(
    format(new Date(new Date().setDate(1)), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  );

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchReport();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users/");
      setUsers(data.users || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {
        startDate,
        endDate,
      };
      
      if (selectedUserId) {
        params.userId = selectedUserId;
      }

      const { data } = await api.get("/sales/report", { params });
      setReportData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchReport();
  };

  const handleOpenDetailsModal = (userId, userName) => {
    setSelectedUserForDetails({ userId, userName });
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedUserForDetails(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const getAchievementColor = (achievement) => {
    if (achievement >= 100) return classes.successCell;
    if (achievement >= 70) return classes.warningCell;
    return classes.errorCell;
  };

  const calculateTotals = () => {
    const totals = reportData.reduce(
      (acc, row) => {
        acc.goal += parseFloat(row.salesGoal) || 0;
        acc.sales += parseFloat(row.totalSales) || 0;
        return acc;
      },
      { goal: 0, sales: 0 }
    );
    
    totals.achievement = totals.goal > 0 
      ? ((totals.sales / totals.goal) * 100).toFixed(2) 
      : 0;
    
    return totals;
  };

  const totals = reportData.length > 0 ? calculateTotals() : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        {i18n.t("users.salesReport.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Paper className={classes.filterSection} elevation={0}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <TextField
                label={i18n.t("users.salesReport.filters.startDate")}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label={i18n.t("users.salesReport.filters.endDate")}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>
                  {i18n.t("users.salesReport.user")}
                </InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <MenuItem value="">
                    {i18n.t("users.salesReport.filters.allUsers")}
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFilter}
                fullWidth
                disabled={loading}
              >
                {i18n.t("users.salesReport.filters.filter")}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : reportData.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              {i18n.t("users.salesReport.noData")}
            </Typography>
          </Box>
        ) : (
          <div className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    {i18n.t("users.salesReport.user")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("users.salesReport.salesGoal")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("users.salesReport.totalSales")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("users.salesReport.achievement")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("users.salesReport.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row) => {
                  const achievement = row.salesGoal > 0
                    ? ((row.totalSales / row.salesGoal) * 100).toFixed(2)
                    : 0;
                  
                  return (
                    <TableRow key={row.userId}>
                      <TableCell align="center">{row.userName}</TableCell>
                      <TableCell align="center">
                        {formatCurrency(row.salesGoal)}
                      </TableCell>
                      <TableCell align="center">
                        {formatCurrency(row.totalSales)}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={getAchievementColor(achievement)}
                      >
                        {achievement}%
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={i18n.t("users.salesReport.viewDetails")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDetailsModal(row.userId, row.userName)}
                          >
                            <SearchIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {totals && (
                  <TableRow className={classes.totalRow}>
                    <TableCell align="center">TOTAL</TableCell>
                    <TableCell align="center">
                      {formatCurrency(totals.goal)}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(totals.sales)}
                    </TableCell>
                    <TableCell
                      align="center"
                      className={getAchievementColor(totals.achievement)}
                    >
                      {totals.achievement}%
                    </TableCell>
                    <TableCell align="center">-</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>

      {/* Modal de Detalhes de Vendas */}
      {selectedUserForDetails && (
        <SalesDetailsModal
          open={detailsModalOpen}
          onClose={handleCloseDetailsModal}
          userId={selectedUserForDetails.userId}
          userName={selectedUserForDetails.userName}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </Dialog>
  );
};

export default SalesReportModal;
